import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AcademicProfileResume, CalendarEvents, CertificateSection } from '../model/learn.model';
import { Observable, map } from 'rxjs';
import { environment } from '@env/environment';

@Injectable({ providedIn: 'root' })
export class LearnApi {
    private baseUrl = environment.envVar.API_URL;

    constructor(private http: HttpClient) { }

    getAcademicProfileResume(companyId: number): Observable<AcademicProfileResume> {
        return this.http.get<any>(`${this.baseUrl}/company/${companyId}/academic-profile`).pipe(
            map((response) => AcademicProfileResume.fromJson(response))
        );
    }

    /**
     * Certificados emitidos a la cuenta, leídos de course_progress_histories. Incluye los de
     * contenido que ya no está en el catálogo del alumno, que las listas de inscritos no traen.
     */
    getLatestCertificates(companyId: number): Observable<CertificateSection> {
        return this.http.get<any>(`${this.baseUrl}/company/${companyId}/academic-profile/latest-certificates`).pipe(
            map((response) => CertificateSection.fromJson(response))
        );
    }

    getCalendarEvents(companyId: number, start_date: string, end_date: string): Observable<CalendarEvents> {
        return this.http.get<any>(`${this.baseUrl}/calendar/events/?start_date=${start_date}&end_date=${end_date}&company_id=${companyId}`).pipe(
            map((response) => CalendarEvents.fromJson(response))
        );
    }

    // ===== Perfil profesional: experiencia / educación / certificaciones externas =====
    // Independiente de GrikySkills (dominio Learning, ver api-2 app/Domains/Learning).

    getExperiences(companyId: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/learning/companies/${companyId}/experiences`);
    }

    createExperience(companyId: number, data: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/learning/companies/${companyId}/experiences`, data);
    }

    updateExperience(companyId: number, experienceId: number, data: any): Observable<any> {
        return this.http.put<any>(`${this.baseUrl}/learning/companies/${companyId}/experiences/${experienceId}`, data);
    }

    deleteExperience(companyId: number, experienceId: number): Observable<any> {
        return this.http.delete<any>(`${this.baseUrl}/learning/companies/${companyId}/experiences/${experienceId}`);
    }

    getEducation(companyId: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/learning/companies/${companyId}/education`);
    }

    createEducation(companyId: number, data: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/learning/companies/${companyId}/education`, data);
    }

    updateEducation(companyId: number, educationId: number, data: any): Observable<any> {
        return this.http.put<any>(`${this.baseUrl}/learning/companies/${companyId}/education/${educationId}`, data);
    }

    deleteEducation(companyId: number, educationId: number): Observable<any> {
        return this.http.delete<any>(`${this.baseUrl}/learning/companies/${companyId}/education/${educationId}`);
    }

    getExternalCertifications(companyId: number): Observable<any[]> {
        return this.http.get<any[]>(`${this.baseUrl}/learning/companies/${companyId}/external-certifications`);
    }

    createExternalCertification(companyId: number, data: any): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/learning/companies/${companyId}/external-certifications`, data);
    }

    updateExternalCertification(companyId: number, certificationId: number, data: any): Observable<any> {
        return this.http.put<any>(`${this.baseUrl}/learning/companies/${companyId}/external-certifications/${certificationId}`, data);
    }

    deleteExternalCertification(companyId: number, certificationId: number): Observable<any> {
        return this.http.delete<any>(`${this.baseUrl}/learning/companies/${companyId}/external-certifications/${certificationId}`);
    }

    // ===== Perfil público (independiente de GrikySkills) =====

    shareProfile(companyId: number): Observable<{ slug: string }> {
        return this.http.post<{ slug: string }>(`${this.baseUrl}/learning/companies/${companyId}/share-profile`, {});
    }

    getPublicProfile(slug: string): Observable<any> {
        return this.http.get<any>(`${this.baseUrl}/learning/public-profile/${slug}`);
    }

    // ===== Importar perfil desde CV (PDF/.docx) =====

    createCvImport(companyId: number, file: File): Observable<{ id: number; status: string }> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<{ id: number; status: string }>(
            `${this.baseUrl}/learning/companies/${companyId}/cv-imports`, formData
        );
    }

    getCvImportStatus(companyId: number, cvImportId: number): Observable<{
        status: 'processing' | 'done' | 'failed';
        data?: { experiences: any[]; education: any[]; externalCertifications: any[] };
        errorMessage?: string;
    }> {
        return this.http.get<any>(`${this.baseUrl}/learning/companies/${companyId}/cv-imports/${cvImportId}`);
    }
}
